import { createSlice } from "@reduxjs/toolkit";
import { hideLoading, showLoading } from "react-redux-loading-bar";

import {
  createFaqGroup,
  deleteFaqGroup,
  getFaq,
  updateFaqGroup,
} from "./faqAPI";

export const faqErrorMessage = (error) =>
  error.response?.data?.detail ??
  error.response?.data?.message ??
  error.message ??
  "Request failed";

// The tree keys categories as "get-started"; the API names them GET_STARTED.
export const categoryName = (categoryKey) => categoryKey.toUpperCase().replace(/-/g, "_");

export const emptyGroup = (categoryKey, order) => ({
  category: categoryName(categoryKey),
  slug: "",
  order,
  title: { en: "", zht: "" },
  questions: [],
});

// Counting the groups repeats an order once one in the middle has been deleted, and the
// API sorts on it, so the two would swap places between requests.
export const nextOrder = (groups) => Math.max(-1, ...groups.map(({ order }) => order)) + 1;

export const emptyQuestion = () => ({ question: { en: "", zht: "" }, answer: { en: "", zht: "" } });

export const moveItem = (list, index, offset) => {
  const target = index + offset;
  if (target < 0 || target >= list.length) return list;
  const next = [...list];
  [next[index], next[target]] = [next[target], next[index]];
  return next;
};

export const findGroup = (categories, groupId) =>
  categories.flatMap((category) => category.groups).find(({ id }) => id === groupId) ?? null;

// A parkit-server that predates the page field only served the FAQ page, so its categories
// default there rather than under a blank heading linking to the site root.
const DEFAULT_PAGE = { key: "faq", path: "/faq" };

// Each category renders on exactly one website page, and the sidebar groups by that so an
// admin can tell a /faq category from a /cancellation-policy one before editing it. Keyed
// rather than run-length grouped, so a page keeps one block however the server orders it.
export const pagesOf = (categories) =>
  [
    ...categories
      .reduce((pages, category) => {
        const { key, path } = category.page ?? DEFAULT_PAGE;
        const page = pages.get(key) ?? { key, path, categories: [] };
        page.categories.push(category);
        return pages.set(key, page);
      }, new Map())
      .values(),
  ];

const initialState = {
  categories: [],
  status: "idle",
  error: "",
  successMessage: "",
  selectedGroupId: null,
  editingGroup: null,
  editingQuestion: null,
  deleteGroup: null,
  pending: false,
};

export const faqSlice = createSlice({
  name: "faq",
  initialState,
  reducers: {
    setCategories: (state, action) => {
      state.categories = action.payload;
      if (!findGroup(action.payload, state.selectedGroupId)) {
        state.selectedGroupId = action.payload.flatMap((category) => category.groups)[0]?.id ?? null;
      }
    },
    setStatus: (state, action) => {
      state.status = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    setSuccessMessage: (state, action) => {
      state.successMessage = action.payload;
    },
    setSelectedGroupId: (state, action) => {
      state.selectedGroupId = action.payload;
    },
    setEditingGroup: (state, action) => {
      state.editingGroup = action.payload;
    },
    setEditingQuestion: (state, action) => {
      state.editingQuestion = action.payload;
    },
    setDeleteGroup: (state, action) => {
      state.deleteGroup = action.payload;
    },
    setPending: (state, action) => {
      state.pending = action.payload;
    },
  },
});

export const {
  setCategories,
  setStatus,
  setError,
  setSuccessMessage,
  setSelectedGroupId,
  setEditingGroup,
  setEditingQuestion,
  setDeleteGroup,
  setPending,
} = faqSlice.actions;

export const selectFaq = (state) => state.faq;

export const handleGetFaq = () => async (dispatch) => {
  dispatch(setStatus("loading"));
  try {
    const response = await getFaq();
    dispatch(setCategories(response.data));
    dispatch(setStatus("succeeded"));
  } catch (error) {
    dispatch(setError(faqErrorMessage(error)));
    dispatch(setStatus("failed"));
  }
};

const mutation = (request, success, onDone) => async (dispatch) => {
  dispatch(showLoading());
  dispatch(setPending(true));
  let failure = "";
  try {
    const response = await request();
    onDone?.(dispatch, response);
    dispatch(setSuccessMessage(success));
    return true;
  } catch (error) {
    failure = faqErrorMessage(error);
    return false;
  } finally {
    // The refetch reports its own failures, so a failed save has to reassert its
    // message afterwards or lose it whenever the refetch fails too.
    await dispatch(handleGetFaq());
    if (failure) dispatch(setError(failure));
    dispatch(setPending(false));
    dispatch(hideLoading());
  }
};

export const handleSaveGroup = (group) =>
  mutation(
    () => (group.id ? updateFaqGroup(group) : createFaqGroup(group)),
    `Saved ${group.title.en || group.slug || "group"}.`,
    (dispatch, response) => {
      dispatch(setEditingGroup(null));
      if (!group.id) dispatch(setSelectedGroupId(response.data.id));
    }
  );

export const handleDeleteGroup = (group) =>
  mutation(
    () => deleteFaqGroup(group.id),
    `Deleted ${group.title.en || group.slug}.`,
    (dispatch) => {
      dispatch(setDeleteGroup(null));
      dispatch(setSelectedGroupId(null));
    }
  );

export const handleSaveQuestions = (group, questions, success) =>
  mutation(
    () => updateFaqGroup({ ...group, questions }),
    success,
    (dispatch) => dispatch(setEditingQuestion(null))
  );

export default faqSlice.reducer;
